(() => {
 if (customElements.get('rp-contact-upload')) return;
 let turnstileLoader;
 function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!turnstileLoader) turnstileLoader = new Promise((resolve, reject) => {
   const callback = 'restopackTurnstileReady';
   const script = document.createElement('script');
   const failed = () => { clearTimeout(timer); turnstileLoader = null; script.remove(); delete window[callback]; reject(new Error('Security check could not load. Please retry.')); };
   const timer = setTimeout(failed, 25000);
   window[callback] = () => { clearTimeout(timer); resolve(window.turnstile); delete window[callback]; };
   script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=' + callback + '&render=explicit';
   script.async = true;
   script.onerror = failed;
   document.head.append(script);
  });
  return turnstileLoader;
 }
 class ContactUpload extends HTMLElement {
  connectedCallback() {
   if (this.initialized) return;
   this.initialized = true; this.entries = []; this.busy = false;
   this.endpoint = this.dataset.endpoint.replace(/\/$/, '');
   this.input = this.querySelector('input[type=file]');
   this.list = this.querySelector('ul'); this.status = this.querySelector('[role=status]');
   this.hiddenInput = this.querySelector('input[type=hidden]');
   this.challengeBox = this.querySelector('.rp-contact-upload__challenge');
   this.challengeBox.hidden = true;
   this.form = this.closest('form'); this.abortController = new AbortController();
   const opts = { signal: this.abortController.signal };
   this.input.addEventListener('change', () => { this.addFiles(this.input.files); this.input.value = ''; }, opts);
   const drop = this.querySelector('.rp-contact-upload__drop');
   drop.addEventListener('dragover', e => { e.preventDefault(); drop.classList.add('is-dragging'); }, opts);
   drop.addEventListener('dragleave', () => drop.classList.remove('is-dragging'), opts);
   drop.addEventListener('drop', e => { e.preventDefault(); drop.classList.remove('is-dragging'); this.addFiles(e.dataTransfer.files); }, opts);
   this.form.addEventListener('submit', e => {
    if (this.entries.some(item => item.state !== 'done')) {
     e.preventDefault(); e.stopImmediatePropagation();
     this.announce('Please wait for uploads to finish, or retry / remove the files that failed.');
     this.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
   }, { capture: true, signal: this.abortController.signal });
   this.hidden = false;
  }
  disconnectedCallback() {
   this.abortController?.abort();
   for (const entry of this.entries || []) entry.xhr?.abort();
   if (this.widget !== undefined && window.turnstile) window.turnstile.remove(this.widget);
   this.rejectChallenge?.();
  }
  announce(text) { this.status.textContent = text; }
  addFiles(files) {
   const errors = [];
   for (const file of files) {
    if (this.entries.length >= 5) { errors.push('You can attach up to 5 files.'); break; }
    if (!/\.(jpe?g|png|webp|pdf|ai|psd)$/i.test(file.name)) { errors.push(file.name + ': unsupported file type.'); continue; }
    if (!file.size || file.size > 50 * 1024 * 1024) { errors.push(file.name + ': must be between 1 byte and 50 MB.'); continue; }
    if (this.entries.some(e => e.file.name === file.name && e.file.size === file.size && e.file.lastModified === file.lastModified)) continue;
    const entry = { file, state: 'queued' }; this.entries.push(entry);
    const row = document.createElement('li'); const info = document.createElement('div');
    const name = document.createElement('span'); name.textContent = file.name; info.append(name);
    const state = document.createElement('span'); state.className = 'caption-large'; state.textContent = 'Waiting to upload'; info.append(state);
    const progress = document.createElement('progress'); progress.max = 100; progress.value = 0; progress.setAttribute('aria-label', 'Upload progress for ' + file.name); info.append(progress);
    const retry = document.createElement('button'); retry.type = 'button'; retry.className = 'link'; retry.textContent = 'Retry'; retry.hidden = true;
    retry.addEventListener('click', () => { entry.state = 'queued'; retry.hidden = true; this.process(); });
    const remove = document.createElement('button'); remove.type = 'button'; remove.className = 'link'; remove.textContent = 'Remove'; remove.setAttribute('aria-label', 'Remove ' + file.name);
    remove.addEventListener('click', () => {
     const wasDone = entry.state === 'done'; entry.state = 'removed'; entry.xhr?.abort(); row.remove();
     this.entries = this.entries.filter(e => e !== entry); this.sync();
     if (entry.ticket && wasDone) fetch(this.endpoint + '/upload', { method: 'DELETE', headers: { Authorization: 'Bearer ' + entry.ticket } }).catch(() => {});
     this.announce(this.entries.length ? 'File removed.' : 'No attachments selected.');
    });
    row.append(info, retry, remove); this.list.append(row); Object.assign(entry, { row, stateLabel: state, progress, retry });
   }
   this.announce(errors.join(' ')); this.process();
  }
  async challenge() {
   this.challengeBox.hidden = false;
   this.announce('Complete the security check below to start uploading.');
   const api = await loadTurnstile();
   return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (error, token) => {
     if (settled) return;
     settled = true; clearTimeout(timer);
     this.resolveChallenge = null; this.rejectChallenge = null;
     if (error) reject(error); else resolve(token);
    };
    const timer = setTimeout(() => finish(new Error('Security check timed out. Click Retry and complete the check.')), 45000);
    this.resolveChallenge = token => finish(null, token);
    this.rejectChallenge = () => finish(new Error('Security check failed. Click Retry to try again.'));
    if (this.widget === undefined) this.widget = api.render(this.querySelector('.rp-contact-upload__challenge'), {
     sitekey: this.dataset.sitekey, action: 'contact_upload', size: this.clientWidth < 300 ? 'compact' : 'flexible', theme: 'light',
     appearance: 'always', retry: 'never',
     'response-field': false,
     callback: token => this.resolveChallenge?.(token),
     'error-callback': () => this.rejectChallenge?.(),
     'timeout-callback': () => this.rejectChallenge?.(),
     'expired-callback': () => this.rejectChallenge?.()
    });
    else api.reset(this.widget);
   });
  }
  async process() {
   if (this.busy || !this.isConnected) return;
   const batch = this.entries.filter(e => e.state === 'queued'); if (!batch.length) return;
   this.busy = true;
   batch.forEach(e => { e.state = 'verifying'; e.stateLabel.textContent = 'Waiting for security verification…'; });
   try {
    const turnstileToken = await this.challenge();
    this.challengeBox.hidden = true;
    batch.forEach(e => { if (e.state !== 'removed') e.stateLabel.textContent = 'Starting upload…'; });
    const sessionController = new AbortController();
    const abortSession = () => sessionController.abort();
    this.abortController.signal.addEventListener('abort', abortSession, { once: true });
    const sessionTimer = setTimeout(abortSession, 30000);
    let data;
    try {
    const response = await fetch(this.endpoint + '/session', {
     method: 'POST', headers: { 'Content-Type': 'application/json' }, signal: sessionController.signal,
     body: JSON.stringify({ turnstileToken, files: batch.map(e => ({ name: e.file.name, size: e.file.size })) })
    });
    data = await response.json(); if (!response.ok) throw new Error(data.error || 'Could not start upload.');
    } catch (error) {
     if (sessionController.signal.aborted) throw new Error('Upload connection timed out. Please retry.');
     throw error;
    } finally { clearTimeout(sessionTimer); this.abortController.signal.removeEventListener('abort', abortSession); }
    for (let i = 0; i < batch.length; i++) {
     const entry = batch[i]; if (entry.state === 'removed') continue;
     entry.ticket = data.tickets[i].token;
     try {
      const result = await this.upload(entry); if (entry.state === 'removed') continue;
      entry.state = 'done'; entry.url = result.url; entry.stateLabel.textContent = 'Uploaded'; entry.progress.value = 100;
     } catch (error) { if (entry.state !== 'removed') this.fail(entry, error.message); }
     this.sync();
    }
   } catch (error) { batch.forEach(e => { if (e.state !== 'removed' && e.state !== 'done') this.fail(e, error.message); }); }
   finally {
    this.challengeBox.hidden = true;
    this.busy = false; this.sync();
    if (this.entries.length && this.entries.every(e => e.state === 'done')) this.announce('Attachments uploaded and ready to send with your request.');
    this.process();
   }
  }
  upload(entry) {
   return new Promise((resolve, reject) => {
    entry.state = 'uploading'; const xhr = new XMLHttpRequest(); entry.xhr = xhr;
    xhr.open('PUT', this.endpoint + '/upload'); xhr.setRequestHeader('Authorization', 'Bearer ' + entry.ticket);
    xhr.setRequestHeader('Content-Type', 'application/octet-stream'); xhr.timeout = 300000;
    xhr.upload.onprogress = event => { if (event.lengthComputable) { const value = Math.round(event.loaded / event.total * 100); entry.progress.value = value; entry.stateLabel.textContent = value < 100 ? 'Uploading ' + value + '%' : 'Finishing upload…'; } };
    xhr.onload = () => { let result; try { result = JSON.parse(xhr.responseText); } catch { reject(new Error('Upload failed. Please retry.')); return; } if (xhr.status >= 200 && xhr.status < 300) resolve(result); else reject(new Error(result.error || 'Upload failed. Please retry.')); };
    xhr.onerror = () => reject(new Error('Connection interrupted. Please retry.'));
    xhr.ontimeout = () => reject(new Error('Upload timed out. Please retry.'));
    xhr.onabort = () => reject(new Error('Upload cancelled.'));
    xhr.send(entry.file);
   });
  }
  fail(entry, message) { entry.state = 'error'; entry.stateLabel.textContent = message; entry.retry.hidden = false; this.announce('An attachment could not be uploaded. Retry or remove it before submitting.'); }
  sync() { this.hiddenInput.value = this.entries.filter(e => e.state === 'done').map(e => e.file.name + '\n' + e.url).join('\n\n'); this.dispatchEvent(new CustomEvent('rp:attachments-change', { bubbles: true })); }
 }
 customElements.define('rp-contact-upload', ContactUpload);
})();
